import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Icon,
  Input,
  InputLabel,
  LinearProgress,
  LinearProgressProps,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import React, { useRef, useState } from 'react';
import * as assets from '../../../../../../../assets';
import { useTranslation } from 'react-i18next';
import Api, { ApiError, WorkflowData } from '../../../../../../../data/api/Api';
import { useSnackbar } from 'notistack';
import { AttachDataset } from '../AttachDataset';
import { supportedFileTypes } from '../../../../dataset/createDataset/DatasetModal';
import {
  FILE_COMPLITION_PERCENTAGE,
  MAX_FILE_SIZE,
  MAX_LENGTH_DATASET_NAME,
  handleEnterOrSpace,
} from '../../../../../../../components/staticComponents/StaticHtmlGenerator';
import { AppSkillId } from '../../../../../../../components/staticComponents/StaticHtmlGenerator';
import { useAppSelector, useAppDispatch } from '../../../../../../../reduxStore/redux-hooks';
import TopKConfiguration from '../../../../../../sharedComponents/TopKConfiguration';
import {
  findEmptyFileName,
  showSnackbarErrorMessage,
} from '../../../../../../../components/staticComponents/staticUtiles';
import { updateFileSelectionState } from '../../../../../../../reduxStore/slices/FileUploadSlice';
import { LoadingButton } from '@mui/lab';
import NumberInput from '../../../../../../../components/sharedComponents/numberInput/NumberInput';

function AttachNewDataset({ attachDataset, setIsAgentCreateInProcess, selectedExistingDataset }: AttachDataset) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [uploadedFilesInfo, setUploadedFilesInfo] = React.useState<
    { name: string; size: string; progress: number; timeLeft: string }[]
  >([]);
  const [fileUploadInProgress, setFileUploadInProgress] = useState<boolean>(false);
  const [crawlMaxDepth, setCrawlMaxDepth] = useState<number>(0);
  const [crawled, setCrawled] = useState<boolean>(false);
  const [webLinks, setWebLinks] = useState<string>('');
  const { updateAgentDetails, isAgentToBeUpdated, isAgentView } = useAppSelector((state) => state.agents);

  const newDatasetName = useRef<string>('');
  const newDatasetDescription = useRef<string>('');
  const newDatasetId = useRef<string>('');
  const dispatch = useAppDispatch();
  const { fileSelection } = useAppSelector((state) => state.fileUpload);
  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      processFiles(Array.from(files));
    }
  };

  const DropFiles = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files) {
      processFiles(Array.from(files));
    }
  };

  const processFiles = (files: File[]) => {
    const checkIfUploadedFilesSupport = files.every((file) => supportedFileTypes.includes(file.type));
    if (!checkIfUploadedFilesSupport) {
      enqueueSnackbar(t('fileUpload.supportedFiles'), { variant: 'error' });
      return;
    }

    const isFilesSizeAndLengthIsValid = validateFilesLengthAndSize(files);

    if (uploadedFilesInfo.length > 0 && uploadedFilesInfo.some((file) => file.progress < FILE_COMPLITION_PERCENTAGE)) {
      enqueueSnackbar(t('rightPanel.agent.createAgent.attachDataset.datasetPrepareInProgress'), { variant: 'error' });
      return;
    }

    if (isFilesSizeAndLengthIsValid) {
      storeNewDatasetFiles(files);
      createAndPrepareDataset(files);
    }
  };

  const validateFilesLengthAndSize = (files: File[]) => {
    const currentFilesCount = uploadedFilesInfo.length;

    if (findEmptyFileName(files!)) {
      enqueueSnackbar(t('fileUpload.fileIsEmpty', { fileName: findEmptyFileName(files!) }), {
        variant: 'error',
      });
      return;
    }
    const currentFilesTotalSize = uploadedFilesInfo.reduce((total, file) => {
      return total + parseFloat(file.size);
    }, 0);

    const newFilesCount = files.length;
    const newFilesTotalSize = files.reduce((total, file) => {
      return total + file.size / (1024 * 1024);
    }, 0);

    const totalFilesSize = currentFilesTotalSize + newFilesTotalSize;
    const totalFilesCount = currentFilesCount + newFilesCount;

    if (totalFilesCount > 10) {
      enqueueSnackbar(t('fileUpload.uploadMax10Files'), { variant: 'error' });
      return false;
    }
    if (totalFilesSize > MAX_FILE_SIZE) {
      enqueueSnackbar(t('fileUpload.uploadMax100MB'), { variant: 'error' });
      return false;
    }
    return true;
  };

  const createAndPrepareDataset = async (files: File[]) => {
    try {
      const truncatedDatasetName: string =
        files[0].name.length >= MAX_LENGTH_DATASET_NAME
          ? files[0].name.slice(0, MAX_LENGTH_DATASET_NAME)
          : files[0].name;

      setIsAgentCreateInProcess(true);
      const totalApiCallsForCreateAndPrepareDataset = 4;
      const newlyCreatedDatasetId = selectedExistingDataset
        ? selectedExistingDataset._id
        : uploadedFilesInfo.length > 0 || newDatasetId.current
          ? newDatasetId.current
          : (
              await Api.createDataset(
                truncatedDatasetName,
                AppSkillId.DocCompletion,
                fileSelection.complexDocumentsEnabled
              )
            ).data._id;
      calculateFileUploadProgress(totalApiCallsForCreateAndPrepareDataset, 1, files);
      newDatasetId.current = newlyCreatedDatasetId;
      await Api.uploadDataset(newlyCreatedDatasetId, files);
      calculateFileUploadProgress(totalApiCallsForCreateAndPrepareDataset, 2, files);
      const prepare = await Api.prepareDataset(
        newlyCreatedDatasetId,
        AppSkillId.DocCompletion,
        fileSelection.isVisionSummaryEnabled
      );
      const workflowData = prepare.data;
      calculateFileUploadProgress(totalApiCallsForCreateAndPrepareDataset, 3, files);
      // For general-purpose datasets (complexDocumentsEnabled is false), stop after prepare.
      if (fileSelection.complexDocumentsEnabled !== true) {
        calculateFileUploadProgress(totalApiCallsForCreateAndPrepareDataset, 4, files);
        setIsAgentCreateInProcess(false);
        attachDataset(newDatasetId.current);
        dispatch(
          updateFileSelectionState({
            isVisionSummaryEnabled: false,
          })
        );
        return;
      }
      const delay = 1000 * (files.length || 1);
      await workFlowStatusProgress(workflowData, newlyCreatedDatasetId, delay);
      calculateFileUploadProgress(totalApiCallsForCreateAndPrepareDataset, 4, files);
      setIsAgentCreateInProcess(false);
      attachDataset(newDatasetId.current);
      dispatch(
        updateFileSelectionState({
          isVisionSummaryEnabled: false,
        })
      );
    } catch (error) {
      showSnackbarErrorMessage(error as ApiError);
    }
  };

  const workFlowStatusProgress = async (workflowData: WorkflowData, datasetId: string, delay: number) => {
    // Some prepare responses return `workflow_id` instead of `_id`; prefer the real id if present
    const getWorkflowId = (data: WorkflowData) => (data as any).workflow_id || (data as any)._id;
    let workflowId = getWorkflowId(workflowData);
    if (!workflowId) {
      enqueueSnackbar(t('fileUpload.uploadFileMessage.failurePrepareMesssage'), { variant: 'error' });
      return;
    }
    while (workflowData.status !== 'Completed') {
      const workflow = await Api.getWorkflowForDataset(datasetId, workflowId);
      if (workflow.data) {
        workflowData = workflow.data;
        workflowId = getWorkflowId(workflowData) || workflowId; // keep latest id when available
      }
      if (workflowData.status === 'Failed') {
        enqueueSnackbar(workflowData.message, { variant: 'error' });
        setFileUploadInProgress(false);
        return;
      }
      await new Promise((r) => setTimeout(r, delay));
    }
  };

  const storeNewDatasetFiles = (files: File[]) => {
    files.forEach((file) => {
      const newFileInfo = {
        name: file.name,
        size: `${(file.size / (1024 * 1024)).toFixed(2)} MB`, // Convert size to MB
        progress: 0,
        timeLeft: 'Calculating...',
      };

      setUploadedFilesInfo((previuos) => [...previuos, newFileInfo]);
    });
  };

  const calculateFileUploadProgress = (
    totalApiCallsForCreateAndPrepareDataset: number,
    completedCalls: number,
    files: File[]
  ) => {
    const progress = Math.min(
      (completedCalls / totalApiCallsForCreateAndPrepareDataset) * FILE_COMPLITION_PERCENTAGE,
      FILE_COMPLITION_PERCENTAGE
    );
    setUploadedFilesInfo((previuos) => {
      const listOfUploadedFiles = [...previuos];
      const remainingCalls = totalApiCallsForCreateAndPrepareDataset - completedCalls;
      const estimatedTimePerCall = 2500; // Random time for each call
      const remainingTime = Math.ceil((remainingCalls * estimatedTimePerCall) / 1000); // Convert to seconds

      const startIndex = Math.max(0, listOfUploadedFiles.length - files.length);
      for (let i = startIndex; i < listOfUploadedFiles.length; i++) {
        listOfUploadedFiles[i].progress = progress;
        listOfUploadedFiles[i].timeLeft = `${remainingTime}`;
      }

      return listOfUploadedFiles;
    });
  };

  const handleDatasetNameChange = (value: string) => {
    if (value.length === MAX_LENGTH_DATASET_NAME) {
      enqueueSnackbar(t('rightPanel.agent.createAgent.attachDataset.validationErrorMessageForDataSetNameLength'), {
        variant: 'error',
      });
      return;
    }
    newDatasetName.current = value;
  };

  const updateDatasetDetails = () => {
    const newDatasetDetails = {
      name: newDatasetName.current ? newDatasetName.current : uploadedFilesInfo[0].name,
      ...(newDatasetDescription.current && { description: newDatasetDescription.current }),
    };
    try {
      setIsAgentCreateInProcess(true);
      Api.updateDataset(newDatasetId.current, newDatasetDetails);
    } catch (error) {
      showSnackbarErrorMessage(error as ApiError);
      console.error(error);
    }
    setIsAgentCreateInProcess(false);
  };

  const handleWebLinks = async () => {
    try {
      setIsAgentCreateInProcess(true);
      setFileUploadInProgress(true);
      if (!newDatasetId.current && !selectedExistingDataset?._id) {
        newDatasetId.current = (await Api.createDataset(webLinks, AppSkillId.DocCompletion)).data._id;
      }
      const datasetId = newDatasetId.current || selectedExistingDataset?._id;
      if (!datasetId) {
        enqueueSnackbar(t('snackbar.alertMessage.noDataset'));
        return;
      }
      const crawlResponse = await Api.crawlWebsite(datasetId, webLinks, crawlMaxDepth);
      if (crawlResponse.error) {
        throw new Error(`${crawlResponse.error}`);
      }
      const prepare = await Api.prepareDataset(datasetId, AppSkillId.DocCompletion);
      const workflowData = prepare.data;
      await workFlowStatusProgress(workflowData, datasetId, 1000);
      enqueueSnackbar(t('rightPanel.agent.createAgent.attachDataset.crawlSuccess'));
      setCrawled(true);
    } catch (error) {
      if (!uploadedFilesInfo.length && !selectedExistingDataset?._id) {
        await Api.deleteDataset(newDatasetId.current);
        newDatasetId.current = '';
      }
      enqueueSnackbar((error as Error).toString(), { variant: 'error' });
      setCrawled(false);
    }
    setIsAgentCreateInProcess(false);
    setFileUploadInProgress(false);
  };

  const handleWebLinksChange = (webLink: string) => {
    setWebLinks(webLink);
    setCrawled(false);
  };

  const handleDeleteFile = async (fileName: string, selectedFileIndex: number) => {
    setUploadedFilesInfo((previous) => previous.filter((_, index) => index !== selectedFileIndex));
    if (uploadedFilesInfo.length === 1 && !selectedExistingDataset) {
      try {
        await Api.deleteDataset(newDatasetId.current);
        newDatasetId.current = '';
      } catch (error) {
        showSnackbarErrorMessage(error as ApiError);
      }
    } else {
      try {
        const deleteDatasetResourceResponse = await Api.deleteDatasetResource(newDatasetId.current, [fileName]);
        if (deleteDatasetResourceResponse.error) {
          throw new Error(`${deleteDatasetResourceResponse.error}`);
        }
      } catch (error) {
        showSnackbarErrorMessage(error as ApiError);
      }
    }
  };

  const LinearProgressWithLabel = (props: LinearProgressProps & { value: number }) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%' }}>
          <LinearProgress
            variant="determinate"
            {...props}
            sx={{
              backgroundColor: 'lightgray',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #3C82F6 0%, #3C82F6 70.06%)',
              },
            }}
          />
        </Box>
      </Box>
    );
  };
  const DragFiles = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDatasetTypeChange = (event: SelectChangeEvent<'complex' | 'general'>) => {
    const value = event.target.value as string;
    if (value === 'complex') {
      dispatch(
        updateFileSelectionState({
          complexDocumentsEnabled: true,
          isVisionSummaryEnabled: false,
        })
      );
    } else {
      dispatch(
        updateFileSelectionState({
          complexDocumentsEnabled: false,
          isVisionSummaryEnabled: false,
        })
      );
    }
  };

  return (
    <Box sx={{ maxWidth: '615px' }}>
      <FormControl fullWidth sx={{ my: 2 }}>
        <InputLabel id="dataset-type-label">{'Dataset Type'}</InputLabel>
        <Select
          labelId="dataset-type-label"
          id="dataset-type"
          value={fileSelection.complexDocumentsEnabled ? 'complex' : 'general'}
          label={'Dataset Type'}
          onChange={handleDatasetTypeChange}
        >
          <MenuItem value="general">{'General purpose dataset'}</MenuItem>
          <MenuItem value="complex">{'Dataset with complex tables, charts'}</MenuItem>
        </Select>
      </FormControl>
      {!fileSelection.complexDocumentsEnabled && (
        <FormControlLabel
          control={
            <Checkbox
              checked={fileSelection.isVisionSummaryEnabled}
              onChange={(e) =>
                dispatch(
                  updateFileSelectionState({
                    isVisionSummaryEnabled: e.target.checked,
                  })
                )
              }
            />
          }
          label={t('fileUpload.visionSummary')}
          sx={{ mb: 2 }}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
        onDragOver={DragFiles}
        onDrop={DropFiles}
      >
        <label htmlFor="upload-files">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              marginTop: '20px',
              border: '1px dashed var(--Supportive-Notification, #4282FE)',
              borderRadius: '8px',
              alignItems: 'center',
              padding: '14px',
              cursor: fileUploadInProgress ? 'not-allowed' : 'pointer',
              opacity: fileUploadInProgress ? 0.5 : 1,
            }}
          >
            <img src={assets.fileUploadIcon} alt="file upload" />
            <Typography>{t('rightPanel.agent.createAgent.attachDataset.dropFiles')}</Typography>
            <Typography sx={{ color: '#7E8286' }}>{t('rightPanel.agent.createAgent.attachDataset.or')}</Typography>
            <Input
              style={{ display: 'none' }}
              id="upload-files"
              type="file"
              inputProps={{
                multiple: true,
                accept: supportedFileTypes,
              }}
              onChange={handleFileInputChange}
              disabled={fileUploadInProgress}
            />

            <Typography
              sx={{ color: '#256AFF', cursor: 'pointer', fontWeight: 700, textDecorationLine: 'underline' }}
              role="button"
              tabIndex={0}
              aria-label={t('rightPanel.agent.createAgent.attachDataset.browse')}
            >
              {t('rightPanel.agent.createAgent.attachDataset.browse')}
            </Typography>
          </Box>
        </label>
      </Box>

      {uploadedFilesInfo.length > 0 && !isAgentView && (
        <Box sx={{ padding: '20px 0px' }}>
          {uploadedFilesInfo.map((fileInfo, index) => (
            <Box
              key={index}
              sx={{
                background: '#F5F8FF',
                borderRadius: '4px',
                display: 'flex',
                flexDirection: 'column',
                margin: '10px 0px',
                padding: '10px',
                gap: '9px',
              }}
              role="group"
              aria-label={`${fileInfo.name}, size: ${fileInfo.size}, progress: ${Math.round(fileInfo.progress)}%`}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography>{fileInfo.name}</Typography>
                {fileInfo.progress === FILE_COMPLITION_PERCENTAGE && (
                  <img
                    src={assets.deleteIcon}
                    alt="delete icon"
                    onClick={fileUploadInProgress ? undefined : () => handleDeleteFile(fileInfo.name, index)}
                    style={{
                      cursor: fileUploadInProgress ? 'not-allowed' : 'pointer',
                      opacity: fileUploadInProgress ? 0.5 : 1,
                    }}
                    tabIndex={0}
                    aria-label={`Delete ${fileInfo.name}`}
                    onKeyDown={(e) => handleEnterOrSpace(e, () => handleDeleteFile(fileInfo.name, index))}
                  />
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '12px', color: '#0B0C0C', opacity: '0.5' }}>{fileInfo.size}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#0B0C0C', opacity: '0.5' }}>
                  {`${Math.round(fileInfo.progress)}%`}
                </Typography>
              </Box>

              <LinearProgressWithLabel value={fileInfo.progress} />
            </Box>
          ))}
        </Box>
      )}

      {uploadedFilesInfo.length > 0 && !selectedExistingDataset && !isAgentView && (
        <Box>
          <Box sx={{ padding: '10px 0px' }}>
            <Typography sx={{ fontWeight: 700 }}>
              {t('rightPanel.agent.createAgent.attachDataset.datasetNameLabel')}
            </Typography>
            <TextField
              variant="standard"
              fullWidth
              defaultValue={uploadedFilesInfo[0].name.slice(0, MAX_LENGTH_DATASET_NAME)}
              inputProps={{ maxLength: 20 }}
              placeholder={t('rightPanel.agent.createAgent.attachDataset.datasetNameLabelPlaceHolder')}
              onChange={(event) => handleDatasetNameChange(event.target.value)}
              onBlur={updateDatasetDetails}
            />
          </Box>
          <Box sx={{ padding: '10px 0px' }}>
            <Typography sx={{ fontWeight: 700 }}>
              {t('rightPanel.agent.createAgent.attachDataset.datasetDescription')}
            </Typography>
            <TextField
              variant="standard"
              fullWidth
              placeholder={t('rightPanel.agent.createAgent.attachDataset.datasetNameDescriptionPlaceHolder')}
              onChange={(event) => (newDatasetDescription.current = event.target.value)}
              onBlur={updateDatasetDetails}
            />
          </Box>
        </Box>
      )}

      <Box sx={{ paddingTop: '20px' }}>
        <TopKConfiguration topKValue={isAgentToBeUpdated ? (updateAgentDetails.top_k ?? 5) : 5} />
      </Box>

      <Box sx={{ paddingTop: '60px', display: 'flex', alignItems: 'center', gap: '11px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <InputLabel className="mui-form-label">{t('rightPanel.agent.createAgent.attachDataset.webLinks')}</InputLabel>
          <Typography sx={{ fontSize: '14px' }}>
            {t('rightPanel.agent.createAgent.attachDataset.webLinkHelperText')}
          </Typography>
          <TextField
            variant="standard"
            multiline
            fullWidth
            placeholder={t('rightPanel.agent.createAgent.attachDataset.webLinkPlaceHolder')}
            onChange={(event) => handleWebLinksChange(event.target.value)}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <InputLabel className="mui-form-label">
              {t('rightPanel.agent.createAgent.attachDataset.maxDepth')}
            </InputLabel>
            <Tooltip title={t('rightPanel.agent.createAgent.attachDataset.maxDepthHelperText')} arrow>
              <Icon sx={{ display: 'flex', marginLeft: '5px', cursor: 'pointer' }}>
                <InfoIcon sx={{ width: '20px' }} />
              </Icon>
            </Tooltip>
          </Box>
          <NumberInput
            min={0}
            max={2}
            defaultValue={0}
            onChange={(event, newValue) => {
              setCrawlMaxDepth(newValue ? newValue : 0);
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <LoadingButton
            type="button"
            variant="contained"
            sx={{
              textTransform: 'none',
              backgroundColor: '#3C82F6',
              alignSelf: 'flex-end',
              marginLeft: '10px',
              opacity: crawled ? 0.5 : 1,
              cursor: crawled ? 'not-allowed' : 'pointer',
            }}
            loading={fileUploadInProgress}
            disabled={!webLinks}
            onClick={crawled ? undefined : handleWebLinks}
          >
            {crawled
              ? t('rightPanel.agent.createAgent.attachDataset.crawled')
              : t('rightPanel.agent.createAgent.attachDataset.crawl')}
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
}

export default AttachNewDataset;
